/* ==========================================================================
   Magnimoo — Customer Order Tracking
   Single-file vanilla-JS handler. No framework, no build step.

   States rendered into #magnimoo-track-root:
     1. form                      — order # + email fields
     2. loading                   — three-dot pulse
     3. error                     — generic "no shipment found" copy
     4. not-shipped               — order matched, no metaobject yet
     5. shipped-no-events         — shipment created, carrier hasn't scanned
     6. shipped-with-events       — full timeline

   Submission goes to POST /apps/track/lookup (Shopify App Proxy).
   The endpoint always returns HTTP 200; the body's `ok` flag is the signal.

   XSS safety: all untrusted values reach the DOM exclusively via
   `textContent` / `setAttribute`. HTML strings are never assembled with
   server data. Static markup (e.g. inline SVGs) is set via innerHTML only
   when the source string is a hard-coded literal in this file.

   Shopper-facing strings: magnimoo.customer_track.* locale keys, rendered by
   templates/page.track.liquid as data-track-msg-* attributes on
   .magnimoo-track and read with msg() (Task 8b-1, so they can be translated).
   [date], [courier], [watching] and [email] are filled by fill() /
   fillNodes(); values always become text nodes, never HTML.

   See: docs/superpowers/specs/2026-04-16-customer-order-tracking-page-design.md
   ========================================================================== */

(function () {
  'use strict';

  var root = document.getElementById('magnimoo-track-root');
  if (!root) return;

  var container = root.closest('.magnimoo-track');
  var supportEmail = (container && container.dataset && container.dataset.supportEmail) || 'hello@magnimoo.com';

  // ---------- strings (locale keys via data attributes) --------------------

  /** A shopper-facing string from data-track-msg-<name> on .magnimoo-track. */
  function msg(name) {
    return (container && container.getAttribute('data-track-msg-' + name)) || '';
  }

  /** Replace each [name] in `template` with String(values[name]) in one pass. */
  function fill(template, values) {
    return template.replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : match;
    });
  }

  /** Split `template` at each known [name] into a children list for el():
   *  text runs stay strings, values are inserted as given (string or Node). */
  function fillNodes(template, values) {
    var parts = [];
    var pattern = /\[(\w+)\]/g;
    var last = 0;
    var m;
    while ((m = pattern.exec(template)) !== null) {
      if (!Object.prototype.hasOwnProperty.call(values, m[1])) continue;
      if (m.index > last) parts.push(template.slice(last, m.index));
      parts.push(values[m[1]]);
      last = pattern.lastIndex;
    }
    if (last < template.length) parts.push(template.slice(last));
    return parts;
  }

  // ---------- DOM helpers --------------------------------------------------

  /** Create element with optional className; children are either strings
   *  (appended as text nodes) or Node instances. */
  function el(tag, className, children) {
    var n = document.createElement(tag);
    if (className) n.className = className;
    if (children != null) {
      var list = Array.isArray(children) ? children : [children];
      for (var i = 0; i < list.length; i++) {
        var c = list[i];
        if (c == null || c === false) continue;
        if (typeof c === 'string' || typeof c === 'number') {
          n.appendChild(document.createTextNode(String(c)));
        } else {
          n.appendChild(c);
        }
      }
    }
    return n;
  }

  /** Clear a node of all children. */
  function clear(node) {
    while (node.firstChild) node.removeChild(node.firstChild);
  }

  /** Replace all children of `parent` with `node`. */
  function mount(node) {
    clear(root);
    root.appendChild(node);
  }

  /** Inline SVG — source is a hard-coded literal, safe to innerHTML. */
  var SVG_LIBRARY = {
    arrow: '<svg width="24" height="14" viewBox="0 0 24 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M1 7h20m0 0-5-5m5 5-5 5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    'arrow-up-right': '<svg width="10" height="10" viewBox="0 0 10 10" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><path d="M2 8l6-6M3 2h5v5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    box: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M3 7.5l9-4.5 9 4.5v9L12 21l-9-4.5v-9z" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/><path d="M3 7.5L12 12m0 0l9-4.5M12 12v9" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/></svg>',
    scan: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M4 8V5a1 1 0 0 1 1-1h3M20 8V5a1 1 0 0 0-1-1h-3M4 16v3a1 1 0 0 0 1 1h3m12-4v3a1 1 0 0 1-1 1h-3M8 12h8" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>',
    alert: '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg"><circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="1.8"/><path d="M12 8v4m0 3.5v.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round"/></svg>'
  };

  function iconSpan(name, className) {
    var s = document.createElement('span');
    if (className) s.className = className;
    s.setAttribute('aria-hidden', 'true');
    var markup = SVG_LIBRARY[name] || '';
    // Safe: `markup` is always a hard-coded string from SVG_LIBRARY above.
    s.innerHTML = markup;  // eslint-disable-line no-unsanitized/property
    return s;
  }

  function formatShippedAt(iso) {
    if (!iso) return '';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    try {
      return d.toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
    } catch (_) {
      return iso;
    }
  }

  function statusLabel(status) {
    switch (status) {
      case 'delivered':  return msg('status-delivered');
      case 'in_transit': return msg('status-in-transit');
      case 'shipped':    return msg('status-shipped');
      default:           return msg('status-processing');
    }
  }

  function setBusy(isBusy) {
    root.setAttribute('aria-busy', isBusy ? 'true' : 'false');
  }

  // ---------- form ---------------------------------------------------------

  function renderForm(prefill) {
    var orderName = (prefill && prefill.orderName) || '';
    var email = (prefill && prefill.email) || '';

    var form = el('form', 'magnimoo-track-form');
    form.setAttribute('novalidate', '');

    var orderInput = el('input', 'magnimoo-track-form__input');
    orderInput.type = 'text';
    orderInput.name = 'order_name';
    orderInput.inputMode = 'text';
    orderInput.autocomplete = 'off';
    orderInput.placeholder = msg('order-placeholder');
    orderInput.maxLength = 64;
    orderInput.required = true;
    orderInput.value = orderName;
    orderInput.setAttribute('aria-describedby', 'magnimoo-track-order-hint');

    var emailInput = el('input', 'magnimoo-track-form__input');
    emailInput.type = 'email';
    emailInput.name = 'email';
    emailInput.autocomplete = 'email';
    emailInput.placeholder = msg('email-placeholder');
    emailInput.maxLength = 254;
    emailInput.required = true;
    emailInput.value = email;

    var row = el('div', 'magnimoo-track-form__row', [
      el('label', 'magnimoo-track-form__label', [msg('order-label'), orderInput]),
      el('label', 'magnimoo-track-form__label', [msg('email-label'), emailInput])
    ]);

    var hint = el('span', 'magnimoo-track-sr', msg('order-hint'));
    hint.id = 'magnimoo-track-order-hint';

    var submit = el('button', 'magnimoo-track-form__submit', msg('submit'));
    submit.type = 'submit';

    form.appendChild(row);
    form.appendChild(hint);
    form.appendChild(submit);

    form.addEventListener('submit', onSubmit);

    mount(form);

    var firstEmpty = orderName ? (email ? orderInput : emailInput) : orderInput;
    try { firstEmpty.focus({ preventScroll: true }); } catch (_) { firstEmpty.focus(); }
  }

  // ---------- loading ------------------------------------------------------

  function renderLoading() {
    var dots = el('span', 'magnimoo-track-loading__dots', [
      el('span'), el('span'), el('span')
    ]);
    dots.setAttribute('aria-hidden', 'true');

    var box = el('div', 'magnimoo-track-loading', [
      dots,
      el('span', null, msg('loading'))
    ]);
    box.setAttribute('role', 'status');

    mount(box);
  }

  // ---------- result: shared head block -----------------------------------

  function resultHead(badgeStatus, courier, shippedAt, orderName) {
    var meta = el('div', 'magnimoo-track-result__meta');

    var badge = el('span', 'magnimoo-track-result__badge', statusLabel(badgeStatus));
    badge.setAttribute('data-status', badgeStatus);
    meta.appendChild(badge);

    if (courier) {
      meta.appendChild(el('span', null, courier));
    }
    if (shippedAt) {
      meta.appendChild(el('span', 'magnimoo-track-result__shipped-at',
        fill(msg('shipped-on'), { date: formatShippedAt(shippedAt) })));
    }

    return el('div', 'magnimoo-track-result__head', [
      meta,
      el('h2', 'magnimoo-track-result__order', orderName || '')
    ]);
  }

  // ---------- result: shipped:false ---------------------------------------

  function renderNotShipped(data, formValues) {
    var note = el('div', 'magnimoo-track-note magnimoo-track-note--info', [
      iconSpan('box', 'magnimoo-track-note__icon'),
      el('div', 'magnimoo-track-note__body', [
        el('p', null, el('strong', null, msg('preparing-title'))),
        el('p', null, msg('preparing-text'))
      ])
    ]);

    var wrap = el('div', 'magnimoo-track-result', [
      resultHead('processing', '', '', data.order_name || ''),
      note,
      resetButton(msg('track-another'), formValues)
    ]);
    mount(wrap);
  }

  // ---------- result: shipped (with or without events) --------------------

  function renderShipped(data, formValues) {
    var badgeStatus = data.status || 'shipped';
    var hasEvents = Array.isArray(data.events) && data.events.length > 0;
    var wrap = el('div', 'magnimoo-track-result');

    wrap.appendChild(resultHead(badgeStatus, data.courier || '', data.shipped_at || '', data.order_name || ''));

    // Route
    if (data.from || data.to) {
      wrap.appendChild(el('div', 'magnimoo-track-result__route', [
        el('span', 'magnimoo-track-result__route-label', msg('route-label')),
        el('span', null, data.from || '—'),
        iconSpan('arrow', 'magnimoo-track-result__route-arrow'),
        el('span', null, data.to || '—')
      ]));
    }

    // Tracking number pill + carrier link
    if (data.tracking_number) {
      var trackingRow = el('div', 'magnimoo-track-result__tracking', [
        el('span', 'magnimoo-track-result__tn-label', msg('tracking-number-label')),
        el('span', 'magnimoo-track-result__tn', data.tracking_number)
      ]);
      if (data.carrier_tracking_url && /^https?:\/\//.test(data.carrier_tracking_url)) {
        var linkLabel = fill(msg('view-on-carrier'), { courier: data.courier || msg('carrier-fallback') }) + ' ';
        var link = el('a', 'magnimoo-track-result__carrier-link', linkLabel);
        // setAttribute ensures the URL is treated as an attribute value, not HTML.
        link.setAttribute('href', data.carrier_tracking_url);
        link.setAttribute('target', '_blank');
        link.setAttribute('rel', 'noopener noreferrer');
        link.appendChild(iconSpan('arrow-up-right'));
        trackingRow.appendChild(link);
      }
      wrap.appendChild(trackingRow);
    }

    // Timeline or waiting-for-scan note
    if (hasEvents) {
      var list = el('ol', 'magnimoo-track-timeline');
      for (var i = 0; i < data.events.length; i++) {
        var ev = data.events[i] || {};
        var item = el('li', 'magnimoo-track-timeline__item');
        item.style.animationDelay = (i * 60) + 'ms';
        if (ev.date)     item.appendChild(el('span', 'magnimoo-track-timeline__date', String(ev.date)));
        if (ev.event)    item.appendChild(el('span', 'magnimoo-track-timeline__event', String(ev.event)));
        if (ev.location) item.appendChild(el('span', 'magnimoo-track-timeline__location', String(ev.location)));
        list.appendChild(item);
      }
      wrap.appendChild(list);
    } else {
      wrap.appendChild(el('div', 'magnimoo-track-note', [
        iconSpan('scan', 'magnimoo-track-note__icon magnimoo-track-note__icon--pulse'),
        el('div', 'magnimoo-track-note__body', [
          el('p', null, fillNodes(msg('first-scan-from'), {
            watching: el('strong', null, msg('first-scan-watching')),
            courier: data.courier || msg('the-carrier-fallback')
          })),
          el('p', null, msg('first-scan-text'))
        ])
      ]));
    }

    wrap.appendChild(resetButton(msg('track-another'), formValues));
    mount(wrap);
  }

  // ---------- result dispatcher -------------------------------------------

  function renderResult(data, formValues) {
    if (data.shipped === false) return renderNotShipped(data, formValues);
    return renderShipped(data, formValues);
  }

  // ---------- error --------------------------------------------------------

  function renderError(formValues, kind) {
    var body = el('div', 'magnimoo-track-note__body');

    if (kind === 'network') {
      body.appendChild(el('p', null, el('strong', null, msg('network-error-title'))));
      body.appendChild(el('p', null, msg('network-error-text')));
    } else {
      body.appendChild(el('p', null, el('strong', null, msg('not-found-title'))));
      var supportLink = el('a', null, supportEmail);
      supportLink.setAttribute('href', 'mailto:' + supportEmail);
      body.appendChild(el('p', null, fillNodes(msg('not-found-help'), { email: supportLink })));
    }

    var note = el('div', 'magnimoo-track-note magnimoo-track-note--error', [
      iconSpan('alert', 'magnimoo-track-note__icon'),
      body
    ]);

    var wrap = el('div', 'magnimoo-track-result', [
      note,
      resetButton(msg('try-again'), formValues)
    ]);
    mount(wrap);
  }

  // ---------- reset button -------------------------------------------------

  function resetButton(label, formValues) {
    var btn = el('button', 'magnimoo-track-reset', label);
    btn.type = 'button';
    btn.setAttribute('data-action', 'reset');
    btn.addEventListener('click', function () {
      renderForm(formValues || null);
    });
    return btn;
  }

  // ---------- submission ---------------------------------------------------

  function onSubmit(e) {
    e.preventDefault();
    var form = e.currentTarget;
    var orderInput = form.elements.namedItem('order_name');
    var emailInput = form.elements.namedItem('email');

    var orderName = (orderInput && orderInput.value || '').trim();
    var email     = (emailInput && emailInput.value || '').trim();

    if (!orderName || !email) {
      if (!orderName && orderInput) orderInput.focus();
      else if (!email && emailInput) emailInput.focus();
      return;
    }

    var formValues = { orderName: orderName, email: email };
    var submitBtn = form.querySelector('[type="submit"]');
    if (submitBtn) submitBtn.disabled = true;

    setBusy(true);
    renderLoading();

    fetch('/apps/track/lookup', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify({ order_name: orderName, email: email })
    })
      .then(function (res) {
        if (!res.ok) return Promise.reject(new Error('HTTP ' + res.status));
        return res.json();
      })
      .then(function (data) {
        setBusy(false);
        if (data && data.ok === true) renderResult(data, formValues);
        else renderError(formValues, 'generic');
      })
      .catch(function () {
        setBusy(false);
        renderError(formValues, 'network');
      });
  }

  // ---------- kick off -----------------------------------------------------

  var params = new URLSearchParams(window.location.search);
  var prefillOrder = (params.get('order') || '').trim();
  var prefillEmail = (params.get('email') || '').trim();

  if (prefillOrder && prefillEmail && /.+@.+\..+/.test(prefillEmail)) {
    // Both present and email looks well-formed — prefill and auto-submit.
    renderForm({ orderName: prefillOrder, email: prefillEmail });
    var form = root.querySelector('form');
    if (form) {
      if (typeof form.requestSubmit === 'function') {
        form.requestSubmit();
      } else {
        form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
      }
    }
  } else if (prefillOrder) {
    // Order only — prefill, customer types email.
    renderForm({ orderName: prefillOrder, email: prefillEmail });
  } else {
    renderForm();
  }
})();
